import { PROJECTS } from "@/blyn/legacy/data/projects";
import {
  getProjectWorkspace,
  getProjectTab,
  resolveDeepLink as apiResolveDeepLink,
  listProjects as apiListProjects,
  createProject as apiCreateProject,
  patchProject as apiPatchProject,
  archiveProject as apiArchiveProject,
  addProjectMember as apiAddProjectMember,
  removeProjectMember as apiRemoveProjectMember,
  getMemberCandidates as apiGetMemberCandidates,
  type MemberCandidate,
} from "./blynApiClient";
import { toastApiError } from "./apiToast";
import { getTenantId as resolveTenantId } from "./currentUser";

export type BlynProject = (typeof PROJECTS)[keyof typeof PROJECTS];
export type ProjectStatusFilter = "all" | BlynProject["status"];

export type ProjectCounts = {
  all: number;
  live: number;
  draft: number;
  indexing: number;
  archived: number;
};
export type ProjectMap = Record<string, BlynProject>;

function getTenantId() {
  return resolveTenantId();
}

const _projectCache = new Map<string, BlynProject>();
let _cacheWriteId = 0;
const _projectWriteId = new Map<string, number>();

export function getCachedProject(id: string): BlynProject | null {
  return _projectCache.get(id) ?? null;
}

export function upsertCachedProject(raw: any, index = 0): BlynProject {
  const id = String(raw?.id ?? "");
  const existing = id ? _projectCache.get(id) : undefined;
  const next = normalizeProject({ ...existing, ...raw }, index);
  if (id) {
    _projectWriteId.set(id, ++_cacheWriteId);
    _projectCache.set(id, next);
  }
  return next;
}

const AVATAR_PALETTES = [
  { bg: "rgba(236,191,43,0.18)",  fg: "var(--yellow-text)" },
  { bg: "rgba(88,195,190,0.18)",  fg: "var(--accent-text)" },
  { bg: "rgba(105,211,145,0.18)", fg: "var(--success-text)" },
  { bg: "rgba(176,126,212,0.18)", fg: "#B07ED4" },
  { bg: "rgba(234,112,91,0.18)",  fg: "#E8705A" },
  { bg: "rgba(100,149,237,0.18)", fg: "#6495ED" },
];

const STATUS_LABEL: Record<string, string> = {
  live: "LIVE", draft: "DRAFT", indexing: "INDEXING",
  paused: "PAUSED", scheduled: "SCHED", needs_review: "REVIEW",
};

function normalizeProject(raw: any, index: number): BlynProject {
  const palette = AVATAR_PALETTES[index % AVATAR_PALETTES.length];
  const words = (raw.name || "").trim().split(/\s+/);
  const avatar = words.length >= 2
    ? (words[0][0] + words[1][0]).toUpperCase()
    : (raw.name || "??").slice(0, 2).toUpperCase();
  return {
    ...raw,
    avatar,
    avatarBg: palette.bg,
    avatarFg: palette.fg,
    statusLabel: STATUS_LABEL[raw.status] ?? (raw.status || "DRAFT").toUpperCase(),
    audience: raw.audience_size != null ? raw.audience_size.toLocaleString() : "·",
    drops: raw.drops_count != null ? String(raw.drops_count) : "·",
    spend: raw.budget != null ? `$${raw.budget.toLocaleString()}` : "·",
    spentSoFar: raw.spent != null ? `$${raw.spent.toLocaleString()}` : "·",
    lift: raw.lift_forecast != null ? `${raw.lift_forecast}%` : "·",
    liftActual: raw.lift_actual != null ? `${raw.lift_actual}%` : "·",
  } as unknown as BlynProject;
}

export async function listProjects(
  filter: ProjectStatusFilter = "all"
): Promise<BlynProject[]> {
  try {
    const started = _cacheWriteId;
    const status = filter === "all" ? undefined : filter;
    const res = await apiListProjects(getTenantId(), status);
    const projects = (res.items ?? []).map((p: any, i: number) => normalizeProject(p, i));
    projects.forEach((p) => {
      const lastLocal = _projectWriteId.get(p.id) ?? 0;
      // A patch that landed while this list was in flight is newer — don't
      // clobber the saved name with a stale list payload.
      if (lastLocal > started) {
        const cached = _projectCache.get(p.id);
        if (cached) return;
      }
      _projectCache.set(p.id, p);
    });
    return projects.map((p) => _projectCache.get(p.id) ?? p);
  } catch (err) {
    toastApiError("Failed to load projects", err);
    return [];
  }
}

export async function createProject(body: Record<string, unknown>): Promise<BlynProject | null> {
  try {
    return await apiCreateProject({ tenant_id: getTenantId(), ...body }) as unknown as BlynProject;
  } catch (err) {
    toastApiError("Failed to create project", err);
    return null;
  }
}

export async function patchProject(project_id: string, body: Record<string, unknown>): Promise<BlynProject | null> {
  try {
    const raw = await apiPatchProject(project_id, getTenantId(), body);
    return upsertCachedProject({ id: project_id, ...raw });
  } catch (err) {
    toastApiError("Failed to update project", err);
    return null;
  }
}

export async function archiveProjectById(project_id: string): Promise<boolean> {
  try {
    await apiArchiveProject(project_id, getTenantId());
    return true;
  } catch (err) {
    toastApiError("Failed to archive project", err);
    return false;
  }
}

export async function addProjectMember(project_id: string, name: string, initials: string): Promise<BlynProject | null> {
  try {
    return await apiAddProjectMember(project_id, getTenantId(), name, initials) as unknown as BlynProject;
  } catch (err) {
    toastApiError("Failed to add member", err);
    return null;
  }
}

export async function removeProjectMember(project_id: string, initials: string): Promise<boolean> {
  try {
    await apiRemoveProjectMember(project_id, getTenantId(), initials);
    return true;
  } catch (err) {
    toastApiError("Failed to remove member", err);
    return false;
  }
}

export async function getMemberCandidates(): Promise<MemberCandidate[]> {
  try {
    const res = await apiGetMemberCandidates(getTenantId(), true);
    return res.items ?? [];
  } catch (err) {
    toastApiError("Failed to load member candidates", err);
    return [];
  }
}

export type { MemberCandidate };

export async function getProject(projectId: string): Promise<BlynProject | null> {
  try {
    const liveWorkspace = await getProjectWorkspace(projectId, getTenantId());
    if (liveWorkspace?.project) {
      return {
        id: liveWorkspace.project.id || projectId,
        name: liveWorkspace.project.name,
        status: "live",
      } as BlynProject;
    }
  } catch (err) {
    toastApiError(`Failed to load project ${projectId}`, err);
  }
  return null;
}

export async function getProjectTabData(
  projectId: string,
  tab: string,
  cursor?: string,
  limit = 50
): Promise<{ items: unknown[]; count: number; next_cursor: string | null }> {
  try {
    const raw = await getProjectTab(projectId, tab, getTenantId(), cursor, limit);
    return { ...raw, items: raw.items ?? [] };
  } catch (err) {
    toastApiError(`Failed to load project tab "${tab}"`, err);
    return { items: [], count: 0, next_cursor: null };
  }
}

export async function resolveEntityDeepLink(
  entityType: string,
  entityId: string,
  params?: Record<string, string>
): Promise<string | null> {
  try {
    const res = await apiResolveDeepLink(entityType, entityId, getTenantId(), undefined, params);
    return res.url;
  } catch (err) {
    toastApiError(`Failed to resolve deep link for ${entityType}`, err);
    return null;
  }
}

export function getProjectFixture(_projectId: string | null | undefined): BlynProject | null {
  return null;
}

export function getProjectNameFixture(projectId: string | null | undefined): string {
  return projectId || "";
}

export function listProjectOptionsFixture(): { value: string; label: string }[] {
  return [];
}

export async function getProjectMap(): Promise<ProjectMap> {
  return {};
}

export async function getProjectCounts(): Promise<ProjectCounts> {
  try {
    const [all, live, draft, indexing, archived] = await Promise.all([
      apiListProjects(getTenantId()),
      apiListProjects(getTenantId(), "live"),
      apiListProjects(getTenantId(), "draft"),
      apiListProjects(getTenantId(), "indexing"),
      apiListProjects(getTenantId(), "archived"),
    ]);
    return { all: all.count, live: live.count, draft: draft.count, indexing: indexing.count, archived: archived.count };
  } catch (err) {
    toastApiError("Failed to load project counts", err);
    return { all: 0, live: 0, draft: 0, indexing: 0, archived: 0 };
  }
}
